import Logo from "@/components/Logo";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Users, Kanban, FileText, Plus, X, Phone, Mail, Coins,
  Trash2, ArrowRight, Check, Search, GraduationCap, Sparkles, Loader2, Tag, ClipboardList,
  HeartPulse, Zap, Flame, NotebookPen, Award, Activity, Bot, Copy, LineChart,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import OnboardingDash from "@/components/OnboardingDash";
import AdminSetups from "@/components/AdminSetups";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  const d = r.status === 204 ? null : await r.json().catch(() => null);
  if (!r.ok) throw new Error((d && d.detail) || "Request failed");
  return d;
};

const PIPELINE = [
  { key: "new", label: "New", color: "#d4d4d8" },
  { key: "contacted", label: "Contacted", color: "#6ea8ff" },
  { key: "discovery_call", label: "Discovery", color: "#9fd3ff" },
  { key: "trial", label: "Trial", color: "#e6c67a" },
  { key: "enrolled", label: "Enrolled", color: "#7ee0a1" },
  { key: "lost", label: "Lost", color: "#ff7a7a" },
];
const SOURCE_LABEL = { instagram: "Instagram", tiktok: "TikTok", referral: "Referral", website: "Website", other: "Other" };

function TabBtn({ id, active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg transition-colors ${active === id ? "bg-white/[0.07] text-white" : "text-zinc-400 hover:text-white"}`}
      data-testid={`admin-tab-${id}`}
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} /> {children}
    </button>
  );
}

// ————————————————————————————————————————————————
// CRM Pipeline
// ————————————————————————————————————————————————
function LeadDrawer({ lead, meta, onClose, onChanged }) {
  const [form, setForm] = useState(lead);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(lead), [lead]);

  const patch = async (updates) => {
    setBusy(true);
    try {
      const d = await api(`/crm/leads/${lead.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(updates),
      });
      setForm(d); onChanged(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api(`/crm/leads/${lead.id}/notes`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: note.trim() }),
      });
      const fresh = { ...form, notes: [...(form.notes || []), { text: note.trim(), author: "You", at: new Date().toISOString() }] };
      setForm(fresh); onChanged(fresh); setNote("");
    } catch (e) { toast.error(e.message); }
  };

  const convert = async () => {
    try {
      const d = await api(`/crm/leads/${lead.id}/convert`, { method: "POST" });
      toast.success(d.created_account ? `Account created. Temp password: ${d.temp_password}` : "Lead marked enrolled");
      patch({ status: "enrolled" });
    } catch (e) { toast.error(e.message); }
  };

  const del = async () => {
    try { await api(`/crm/leads/${lead.id}`, { method: "DELETE" }); onChanged(null); onClose(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0A0A0A] border-l border-white/10 flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between px-6 h-16 border-b border-white/8 sticky top-0 bg-[#0A0A0A]">
          <span className="font-heading text-lg font-light">{form.name}</span>
          <button onClick={onClose} data-testid="lead-drawer-close"><X className="h-5 w-5 text-zinc-400" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="space-y-1.5 text-sm text-zinc-400 font-light">
            <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {form.email}</div>
            {form.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {form.phone}</div>}
            <div className="flex items-center gap-2"><Coins className="h-3.5 w-3.5" /> A${form.value || 0} · {SOURCE_LABEL[form.source]}</div>
          </div>

          <div>
            <label className="section-label text-[9px] text-zinc-500">Stage</label>
            <select value={form.status} onChange={(e) => patch({ status: e.target.value })} disabled={busy}
              className="mt-2 w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none" data-testid="lead-status-select">
              {PIPELINE.map((s) => <option key={s.key} value={s.key} className="bg-[#0A0A0A]">{s.label}</option>)}
            </select>
          </div>

          <div>
            <label className="section-label text-[9px] text-zinc-500">Assigned coach</label>
            <select value={form.assigned_coach || ""} onChange={(e) => patch({ assigned_coach: e.target.value || null })} disabled={busy}
              className="mt-2 w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none" data-testid="lead-coach-select">
              <option value="" className="bg-[#0A0A0A]">Unassigned</option>
              {meta.coaches.map((c) => <option key={c.id} value={c.name} className="bg-[#0A0A0A]">{c.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="section-label text-[9px] text-zinc-500">Follow-up</label>
              <input type="date" value={form.follow_up_at ? form.follow_up_at.slice(0, 10) : ""}
                onChange={(e) => patch({ follow_up_at: e.target.value || null })}
                className="mt-2 w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none" data-testid="lead-followup" />
            </div>
            <div>
              <label className="section-label text-[9px] text-zinc-500">Est. value (A$)</label>
              <input type="number" defaultValue={form.value || 0} onBlur={(e) => patch({ value: parseFloat(e.target.value) || 0 })}
                className="mt-2 w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none" data-testid="lead-value" />
            </div>
          </div>

          <label className="flex items-center gap-2.5 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={!!form.payment_pending} onChange={(e) => patch({ payment_pending: e.target.checked })} data-testid="lead-payment-pending" />
            Payment pending
          </label>

          <div>
            <label className="section-label text-[9px] text-zinc-500">Notes</label>
            <div className="mt-2 space-y-2 max-h-52 overflow-y-auto">
              {(form.notes || []).map((n, i) => (
                <div key={i} className="bg-white/[0.03] border border-white/8 rounded-lg p-2.5" data-testid="lead-note">
                  <div className="text-[13px] text-zinc-300 font-light">{n.text}</div>
                  <div className="text-[10px] text-zinc-600 mt-1">{n.author}</div>
                </div>
              ))}
              {(form.notes || []).length === 0 && <div className="text-xs text-zinc-600">No notes yet.</div>}
            </div>
            <div className="mt-2 flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…"
                className="flex-1 bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none" data-testid="lead-note-input" />
              <button onClick={addNote} className="btn-solid px-3 py-2 text-xs" data-testid="lead-note-add">Add</button>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button onClick={convert} className="btn-solid flex-1 py-2.5 text-sm inline-flex items-center justify-center gap-2" data-testid="lead-convert">
              <ArrowRight className="h-4 w-4" /> Convert to student
            </button>
            <button onClick={del} className="btn-glass text-red-300 px-3 py-2.5" data-testid="lead-delete"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewLeadForm({ meta, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", source: "instagram", value: 0 });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const d = await api("/crm/leads", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f) });
      onCreated(d); onClose();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-6" data-testid="new-lead-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md glass-card p-6 space-y-3">
        <div className="flex items-center justify-between"><span className="font-heading text-lg font-light">New lead</span><button type="button" onClick={onClose}><X className="h-5 w-5 text-zinc-400" /></button></div>
        <input required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none" data-testid="new-lead-name" />
        <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none" data-testid="new-lead-email" />
        <input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none" data-testid="new-lead-phone" />
        <div className="grid grid-cols-2 gap-3">
          <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className="bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none" data-testid="new-lead-source">
            {meta.sources.map((s) => <option key={s} value={s} className="bg-[#0A0A0A]">{SOURCE_LABEL[s]}</option>)}
          </select>
          <input type="number" placeholder="Value A$" value={f.value} onChange={(e) => setF({ ...f, value: parseFloat(e.target.value) || 0 })} className="bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none" data-testid="new-lead-value" />
        </div>
        <button disabled={busy} className="btn-solid w-full py-2.5 text-sm disabled:opacity-60" data-testid="new-lead-submit">{busy ? "Creating…" : "Create lead"}</button>
      </form>
    </div>
  );
}

function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], sources: [], coaches: [] });
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const [l, m, s] = await Promise.all([api("/crm/leads"), api("/crm/meta"), api("/crm/stats")]);
      setLeads(l); setMeta(m); setStats(s);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onChanged = (updated) => {
    if (updated === null) { load(); return; }
    setLeads((ls) => ls.map((l) => (l.id === updated.id ? updated : l)));
    api("/crm/stats").then(setStats).catch(() => {});
  };

  const filtered = leads.filter((l) => !q || l.name.toLowerCase().includes(q.toLowerCase()) || l.email.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="crm-pipeline">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {stats && (
          <>
            <div className="glass-card px-4 py-2.5"><span className="section-label text-[9px] text-zinc-500">Leads</span><div className="text-lg font-heading font-light">{stats.total}</div></div>
            <div className="glass-card px-4 py-2.5"><span className="section-label text-[9px] text-zinc-500">Pipeline value</span><div className="text-lg font-heading font-light silver-text">A${stats.pipeline_value}</div></div>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-2 bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2">
            <Search className="h-3.5 w-3.5 text-zinc-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads" className="bg-transparent text-sm text-zinc-200 focus:outline-none w-40" data-testid="crm-search" />
          </div>
          <button onClick={() => setShowNew(true)} className="btn-solid px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="crm-new-lead"><Plus className="h-4 w-4" /> New lead</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        {PIPELINE.map((col) => {
          const items = filtered.filter((l) => l.status === col.key);
          return (
            <div key={col.key} className="min-w-0" data-testid={`crm-col-${col.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="h-2 w-2 rounded-full" style={{ background: col.color }} />
                <span className="section-label text-[9px] text-zinc-300">{col.label}</span>
                <span className="text-[10px] text-zinc-600">{items.length}</span>
              </div>
              <div className="space-y-2.5">
                {items.map((l) => (
                  <button key={l.id} onClick={() => setActive(l)} className="glass-card w-full text-left p-3.5 hover:border-white/25 transition-colors" data-testid="crm-lead-card">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-zinc-100 font-medium truncate">{l.name}</span>
                      {l.payment_pending && <span className="text-[8px] section-label text-[#e6c67a] border border-[#e6c67a]/30 rounded px-1.5 py-0.5">Pay</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate mt-0.5">{l.email}</div>
                    <div className="flex items-center justify-between mt-2 text-[10px] text-zinc-600">
                      <span>{SOURCE_LABEL[l.source]}</span>
                      {l.value > 0 && <span className="text-[#7ee0a1]">A${l.value}</span>}
                    </div>
                    {l.assigned_coach && <div className="text-[10px] text-[#9fd3ff] mt-1">→ {l.assigned_coach}</div>}
                  </button>
                ))}
                {items.length === 0 && <div className="text-[11px] text-zinc-700 py-4 text-center">—</div>}
              </div>
            </div>
          );
        })}
      </div>

      {active && <LeadDrawer lead={active} meta={meta} onClose={() => setActive(null)} onChanged={(u) => { onChanged(u); if (u) setActive(u); }} />}
      {showNew && <NewLeadForm meta={meta} onClose={() => setShowNew(false)} onCreated={(d) => { setLeads((ls) => [d, ...ls]); api("/crm/stats").then(setStats).catch(() => {}); }} />}
    </div>
  );
}

// ————————————————————————————————————————————————
// Members (entitlements)
// ————————————————————————————————————————————————
function healthColor(score) {
  return score >= 70 ? "#7ee0a1" : score >= 40 ? "#e6c67a" : "#ff7a7a";
}

function StudentProfile({ userId }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setP(null);
    api(`/students/${userId}/profile`).then(setP).catch((e) => setErr(e.message));
  }, [userId]);
  if (err) return <div className="mt-3 text-xs text-red-400">{err}</div>;
  if (!p) return <div className="mt-3 text-xs text-zinc-600 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading profile…</div>;
  const h = p.health;
  const color = healthColor(h.score);
  const COMP_LABEL = { recency: "Recent activity", streak: "Streak", lessons: "Lessons", journal: "Journaling", psychology: "Psychology" };
  const COMP_MAX = { recency: 25, streak: 15, lessons: 25, journal: 20, psychology: 15 };
  return (
    <div className="mt-4 rounded-lg bg-white/[0.03] border border-white/8 p-4" data-testid="student-profile">
      <div className="flex flex-wrap items-center gap-5">
        <div className="flex items-center gap-3">
          <div className="h-16 w-16 rounded-full grid place-items-center border-2" style={{ borderColor: color }} data-testid="health-score">
            <span className="font-heading text-xl" style={{ color }}>{h.score}</span>
          </div>
          <div>
            <div className="section-label text-[9px] text-zinc-500">Health Score</div>
            <div className="text-sm font-medium" style={{ color }}>{h.score >= 70 ? "Thriving" : h.score >= 40 ? "Needs a nudge" : "At risk"}</div>
            {p.last_active && <div className="text-[11px] text-zinc-600">Last active {new Date(p.last_active).toLocaleDateString()}</div>}
          </div>
        </div>
        <div className="flex-1 min-w-[220px] space-y-1.5">
          {Object.entries(h.components).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-[11px]">
              <span className="w-24 text-zinc-500">{COMP_LABEL[k]}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(v / COMP_MAX[k]) * 100}%`, background: color }} />
              </div>
              <span className="w-10 text-right text-zinc-400">{v}/{COMP_MAX[k]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {[
          { icon: Zap, label: "XP", value: p.trainer.xp, color: "#e6c67a" },
          { icon: Flame, label: "Streak", value: `${p.trainer.streak}d`, color: "#ff9d5c" },
          { icon: GraduationCap, label: "Stage", value: p.trainer.stage, color: "#9fd3ff" },
          { icon: Check, label: "Lessons", value: p.trainer.completed_count, color: "#7ee0a1" },
          { icon: NotebookPen, label: "Trades", value: p.trade_count, color: "#b98cff" },
          { icon: Activity, label: "Win rate", value: `${p.journal.win_rate || 0}%`, color: "#9fd3ff" },
          { icon: HeartPulse, label: "Psychology", value: p.journal.psychology_score || 0, color: "#ff7a9a" },
        ].map((s) => (
          <span key={s.label} className="inline-flex items-center gap-1.5 text-[11px] border border-white/10 rounded-full px-2.5 py-1.5 text-zinc-300" data-testid={`profile-stat-${s.label.toLowerCase().replace(" ", "-")}`}>
            <s.icon className="h-3 w-3" style={{ color: s.color }} /> {s.label}: <span className="text-white">{s.value}</span>
          </span>
        ))}
      </div>

      {p.badges.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {p.badges.map((b) => (
            <span key={b.id} className="inline-flex items-center gap-1 text-[10px] text-[#e6c67a] border border-[#e6c67a]/30 rounded-full px-2 py-1">
              <Award className="h-3 w-3" /> {b.name}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4">
        <div className="section-label text-[9px] text-zinc-500 mb-2">Activity Timeline</div>
        <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1" data-testid="activity-timeline">
          {p.timeline.length === 0 && <p className="text-xs text-zinc-600">No activity yet.</p>}
          {p.timeline.map((a) => (
            <div key={a.id} className="flex items-center gap-2.5 text-[12px]">
              <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: a.type === "purchase" ? "#7ee0a1" : "#9fd3ff" }} />
              <span className="text-zinc-300 flex-1 truncate">{a.text}</span>
              <span className="text-zinc-600 shrink-0">{new Date(a.at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InviteCodes({ catalog }) {
  const [codes, setCodes] = useState([]);
  const [plan, setPlan] = useState("beginner");
  const [dur, setDur] = useState("forever");
  const [uses, setUses] = useState(1);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("/invites/admin").then(setCodes).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const body = { plan, forever: dur === "forever", days: dur === "forever" ? 30 : parseInt(dur, 10), max_uses: parseInt(uses, 10) || 1, code: custom };
      const d = await api("/invites/admin", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      toast.success(`Code created: ${d.code}`, { description: "Copied to clipboard" });
      try { await navigator.clipboard.writeText(d.code); } catch { /* ignore */ }
      setCustom(""); load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const copy = (c) => navigator.clipboard.writeText(c).then(() => toast.success(`${c} copied`)).catch(() => toast.info(c));
  const kill = async (id) => { try { await api(`/invites/admin/${id}`, { method: "DELETE" }); toast.success("Code deactivated"); load(); } catch (e) { toast.error(e.message); } };

  const plans = (catalog?.plans || []).filter((p) => p.id !== "guest" && p.published);
  return (
    <div className="glass-card p-4 mb-6" data-testid="invite-codes-panel">
      <div className="section-label text-[9px] text-zinc-500 mb-3 flex items-center gap-1.5">
        <Tag className="h-3 w-3" /> Family &amp; Friends Codes — students redeem these on their dashboard for free access
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className="bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none" data-testid="invite-plan-select">
          {plans.map((p) => <option key={p.id} value={p.id} className="bg-[#0A0A0A]">{p.name}</option>)}
        </select>
        <select value={dur} onChange={(e) => setDur(e.target.value)} className="bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none" data-testid="invite-duration-select">
          <option value="forever" className="bg-[#0A0A0A]">Forever (free)</option>
          <option value="30" className="bg-[#0A0A0A]">30 days</option>
          <option value="90" className="bg-[#0A0A0A]">90 days</option>
          <option value="365" className="bg-[#0A0A0A]">1 year</option>
        </select>
        <input type="number" min="1" max="500" value={uses} onChange={(e) => setUses(e.target.value)} title="Max uses"
          className="w-20 bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none" data-testid="invite-uses-input" />
        <input value={custom} onChange={(e) => setCustom(e.target.value.toUpperCase())} placeholder="Custom code (optional)"
          className="w-44 bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none" data-testid="invite-custom-input" />
        <button onClick={create} disabled={busy} className="btn-solid px-4 py-2 text-xs font-medium" data-testid="invite-create-btn">
          {busy ? "Creating…" : "Create code"}
        </button>
      </div>
      {codes.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {codes.map((c) => (
            <div key={c.id} className={`flex flex-wrap items-center gap-3 text-xs rounded-lg px-3 py-2 ${c.active ? "bg-white/[0.03]" : "bg-white/[0.01] opacity-50"}`} data-testid="invite-code-row">
              <button onClick={() => copy(c.code)} className="font-mono text-[#7ee0a1] tracking-widest flex items-center gap-1.5" data-testid={`invite-copy-${c.code}`}>
                <Copy className="h-3 w-3" /> {c.code}
              </button>
              <span className="text-zinc-400">{(catalog?.plans || []).find((p) => p.id === c.plan_id)?.name || c.plan_id}</span>
              <span className="text-zinc-600">{c.forever ? "forever" : `${c.days} days`}</span>
              <span className="text-zinc-600">{c.used_count}/{c.max_uses} used</span>
              {c.uses?.length > 0 && <span className="text-zinc-600 truncate max-w-[220px]">→ {c.uses.map((u) => u.email).join(", ")}</span>}
              {c.active ? (
                <button onClick={() => kill(c.id)} className="ml-auto text-zinc-600 hover:text-red-400 transition-colors" data-testid={`invite-kill-${c.code}`}>deactivate</button>
              ) : <span className="ml-auto text-zinc-700">inactive</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Members() {
  const [members, setMembers] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const [duration, setDuration] = useState("30");
  const [profileFor, setProfileFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([api("/billing/admin/members"), api("/billing/plans")]);
      setMembers(m); setCatalog(c);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setMembership = async (uid, plan, dur) => {
    const d = dur || duration;
    const body = { plan, forever: d === "forever", days: d === "forever" ? 30 : parseInt(d, 10) };
    try { await api(`/billing/admin/members/${uid}/membership`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }); toast.success(d === "forever" && plan !== "guest" ? "Free access granted — never expires" : "Membership updated"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const toggleFeature = async (uid, feature, has) => {
    try { await api(`/billing/admin/members/${uid}/entitlement`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ feature, action: has ? "revoke" : "grant" }) }); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (!catalog) return <div className="text-sm text-zinc-600">Loading…</div>;
  const filtered = members.filter((m) => !q || (m.name || "").toLowerCase().includes(q.toLowerCase()) || m.email.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-members">
      <InviteCodes catalog={catalog} />
      <div className="flex items-center gap-2 mb-5 bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 w-72">
        <Search className="h-3.5 w-3.5 text-zinc-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members" className="bg-transparent text-sm text-zinc-200 focus:outline-none w-full" data-testid="members-search" />
      </div>
      <div className="space-y-2.5">
        {filtered.map((m) => (
          <div key={m.id} className="glass-card overflow-hidden" data-testid="member-row">
            <button onClick={() => setOpen(open === m.id ? null : m.id)} className="w-full flex items-center gap-4 p-4 text-left">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 font-medium">{m.name} <span className="text-zinc-600 text-xs">· {m.role}</span></div>
                <div className="text-[12px] text-zinc-500 truncate">{m.email}</div>
              </div>
              <span className="section-label text-[9px] text-[#9fd3ff] border border-[#9fd3ff]/25 rounded px-2 py-1">{m.entitlements.membership_name}</span>
              <span className="text-[11px] text-zinc-600">{m.entitlements.features.length} features</span>
            </button>
            {open === m.id && (
              <div className="border-t border-white/8 p-4 space-y-4" data-testid="member-detail">
                <div>
                  <label className="section-label text-[9px] text-zinc-500">Membership</label>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <select value={m.entitlements.is_staff ? "" : m.entitlements.membership} disabled={m.entitlements.is_staff}
                      onChange={(e) => setMembership(m.id, e.target.value)}
                      className="w-full sm:w-64 bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none disabled:opacity-50" data-testid="member-plan-select">
                      {m.entitlements.is_staff && <option value="" className="bg-[#0A0A0A]">Staff (full access)</option>}
                      {catalog.plans.map((p) => <option key={p.id} value={p.id} className="bg-[#0A0A0A]">{p.name}{p.price ? ` — A$${p.price}` : ""}</option>)}
                    </select>
                    <select value={duration} disabled={m.entitlements.is_staff}
                      onChange={(e) => setDuration(e.target.value)}
                      className="bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none disabled:opacity-50" data-testid="member-duration-select">
                      <option value="30" className="bg-[#0A0A0A]">30 days</option>
                      <option value="90" className="bg-[#0A0A0A]">90 days</option>
                      <option value="180" className="bg-[#0A0A0A]">180 days</option>
                      <option value="365" className="bg-[#0A0A0A]">1 year</option>
                      <option value="forever" className="bg-[#0A0A0A]">Forever (free access)</option>
                    </select>
                    {!m.entitlements.is_staff && m.entitlements.membership !== "guest" && (
                      <>
                        <button onClick={() => setMembership(m.id, m.entitlements.membership)}
                          className="btn-glass text-white px-3 py-2 text-xs" data-testid="member-apply-duration">
                          Apply duration
                        </button>
                        <span className="text-[11px] text-zinc-500" data-testid="member-expiry">
                          {m.entitlements.expires ? `Expires ${new Date(m.entitlements.expires).toLocaleDateString()}` : "Never expires · complimentary"}
                        </span>
                      </>
                    )}
                  </div>
                  <p className="mt-1.5 text-[11px] text-zinc-600">Pick a plan to grant it for the selected duration. Choose "Forever" to gift permanent free access (family, scholarships).</p>
                </div>
                <div>
                  <label className="section-label text-[9px] text-zinc-500">Feature access (click to grant / revoke)</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(catalog.features).map(([key, label]) => {
                      const has = m.entitlements.features.includes(key);
                      return (
                        <button key={key} disabled={m.entitlements.is_staff} onClick={() => toggleFeature(m.id, key, has)}
                          className={`text-[11px] rounded-full border px-2.5 py-1 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 ${has ? "border-[#7ee0a1]/40 text-[#7ee0a1]" : "border-white/12 text-zinc-500 hover:border-white/30"}`}
                          data-testid={`member-feature-${key}`}>
                          {has && <Check className="h-3 w-3" />} {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {m.role === "student" && (
                  <div>
                    <button onClick={() => setProfileFor(profileFor === m.id ? null : m.id)}
                      className="btn-glass text-white px-3 py-2 text-xs flex items-center gap-1.5" data-testid="view-student-profile">
                      <HeartPulse className="h-3.5 w-3.5" /> {profileFor === m.id ? "Hide student profile" : "Student profile & health score"}
                    </button>
                    {profileFor === m.id && <StudentProfile userId={m.id} />}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————
// Applications (legacy enrolment form submissions)
// ————————————————————————————————————————————————
const APP_STATUSES = [
  { key: "new", label: "New", color: "#d4d4d8" },
  { key: "contacted", label: "Contacted", color: "#6ea8ff" },
  { key: "enrolled", label: "Enrolled", color: "#7ee0a1" },
  { key: "declined", label: "Declined", color: "#ff7a7a" },
];
function Applications() {
  const [apps, setApps] = useState([]);
  useEffect(() => { api("/applications").then(setApps).catch(() => {}); }, []);
  const setStatus = async (id, status) => {
    setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    try { await api(`/applications/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }) }); } catch { /* ignore */ }
  };
  if (apps.length === 0) return <p className="text-zinc-500 font-light" data-testid="admin-empty">No applications yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-list">
      {apps.map((a) => (
        <div key={a.id} className="glass-card p-6" data-testid="admin-app-row">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="text-lg font-light">{a.name}</span>
              <div className="mt-1 text-sm text-zinc-400">{a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
            </div>
            <div className="text-right text-xs text-zinc-500">{a.experience}<br />{a.availability}</div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {APP_STATUSES.map((s) => (
              <button key={s.key} onClick={() => setStatus(a.id, s.key)} style={{ color: s.color }}
                className={`px-3 py-1.5 text-[11px] border transition-colors ${(a.status || "new") === s.key ? "border-white/60" : "border-white/12 hover:border-white/35"}`}
                data-testid={`admin-set-${s.key}-${a.id}`}>{s.label}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ————————————————————————————————————————————————
// Pricing catalog editor
// ————————————————————————————————————————————————
function PricingAdmin() {
  const [cat, setCat] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api("/billing/admin/catalog");
      setCat(d);
      const dr = {};
      [...d.plans, ...d.addons].forEach((it) => { dr[it.id] = JSON.parse(JSON.stringify(it)); });
      setDrafts(dr);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (id, path, val) => {
    setDrafts((prev) => {
      const d = { ...prev, [id]: { ...prev[id] } };
      if (path.includes(".")) {
        const [a, b] = path.split(".");
        d[id][a] = { ...(d[id][a] || {}), [b]: val };
      } else d[id][path] = val;
      return d;
    });
  };

  const save = async (kind, item) => {
    const d = drafts[item.id];
    setSavingId(item.id);
    const updates = { name: d.name, tagline: d.tagline, published: d.published };
    if (item.type === "subscription") { updates.prices = { week: +d.prices.week, month: +d.prices.month, year: +d.prices.year }; updates.year_saving = +d.year_saving || 0; }
    else if (item.type === "program") { updates.price = +d.price; updates.duration_months = +d.duration_months; }
    else if (kind === "addon") { updates.prices = { month: +d.prices.month, year: +d.prices.year }; }
    try { await api(`/billing/admin/catalog/${kind}/${item.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ updates }) }); toast.success(`${d.name} updated`); }
    catch (e) { toast.error(e.message); } finally { setSavingId(null); }
  };

  if (!cat) return <div className="text-sm text-zinc-600">Loading…</div>;
  const numInp = "w-24 bg-white/[0.04] border border-white/12 rounded-lg px-2.5 py-1.5 text-sm text-zinc-200 focus:outline-none";
  const txtInp = "flex-1 min-w-[160px] bg-white/[0.04] border border-white/12 rounded-lg px-3 py-1.5 text-sm text-zinc-200 focus:outline-none";

  const Row = ({ item, kind }) => {
    const d = drafts[item.id] || item;
    return (
      <div className="glass-card p-4" data-testid={`pricing-edit-${item.id}`}>
        <div className="flex flex-wrap items-center gap-2">
          <input value={d.name} onChange={(e) => set(item.id, "name", e.target.value)} className={txtInp} data-testid={`edit-name-${item.id}`} />
          <span className="section-label text-[8px] text-zinc-600 border border-white/12 rounded px-2 py-1">{item.type || kind}</span>
          <label className="flex items-center gap-1.5 text-xs text-zinc-400"><input type="checkbox" checked={!!d.published} onChange={(e) => set(item.id, "published", e.target.checked)} data-testid={`edit-pub-${item.id}`} /> Published</label>
        </div>
        <input value={d.tagline || ""} onChange={(e) => set(item.id, "tagline", e.target.value)} placeholder="Tagline" className={`${txtInp} w-full mt-2`} />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {item.type === "subscription" && (["week", "month", "year"].map((p) => (
            <label key={p} className="text-xs text-zinc-500">{p} A$ <input type="number" value={d.prices?.[p] ?? 0} onChange={(e) => set(item.id, `prices.${p}`, e.target.value)} className={numInp} data-testid={`edit-price-${item.id}-${p}`} /></label>
          )))}
          {item.type === "subscription" && <label className="text-xs text-zinc-500">yr save A$ <input type="number" value={d.year_saving ?? 0} onChange={(e) => set(item.id, "year_saving", e.target.value)} className={numInp} /></label>}
          {item.type === "program" && (<>
            <label className="text-xs text-zinc-500">A$/mo <input type="number" value={d.price ?? 0} onChange={(e) => set(item.id, "price", e.target.value)} className={numInp} data-testid={`edit-price-${item.id}`} /></label>
            <label className="text-xs text-zinc-500">months <input type="number" value={d.duration_months ?? 0} onChange={(e) => set(item.id, "duration_months", e.target.value)} className={numInp} /></label>
            <span className="text-xs text-[#7ee0a1]">Total A${(+d.price || 0) * (+d.duration_months || 0)}</span>
          </>)}
          {kind === "addon" && (["month", "year"].map((p) => (
            <label key={p} className="text-xs text-zinc-500">{p} A$ <input type="number" value={d.prices?.[p] ?? 0} onChange={(e) => set(item.id, `prices.${p}`, e.target.value)} className={numInp} data-testid={`edit-price-${item.id}-${p}`} /></label>
          )))}
          <button onClick={() => save(kind, item)} disabled={savingId === item.id} className="btn-solid px-4 py-1.5 text-xs ml-auto disabled:opacity-60" data-testid={`save-${item.id}`}>{savingId === item.id ? "Saving…" : "Save"}</button>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="admin-pricing">
      <p className="text-sm text-zinc-500 font-light max-w-2xl mb-5">Edit membership and add-on pricing, durations and visibility. Changes take effect immediately across the site and app — no code changes needed.</p>
      <div className="section-label text-[9px] text-zinc-400 mb-3">Memberships</div>
      <div className="space-y-3">{cat.plans.map((p) => <Row key={p.id} item={p} kind="plan" />)}</div>
      <div className="section-label text-[9px] text-zinc-400 mb-3 mt-8">Add-Ons</div>
      <div className="space-y-3">{cat.addons.map((a) => <Row key={a.id} item={a} kind="addon" />)}</div>
    </div>
  );
}


// ————————————————————————————————————————————————
// Curriculum (Trading Trainer)
// ————————————————————————————————————————————————
function GenerateLesson({ seasonId, onCreated }) {
  const [topic, setTopic] = useState("");
  const [level, setLevel] = useState("Beginner");
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (!topic.trim()) { toast("Enter a topic"); return; }
    setBusy(true);
    try {
      const d = await api("/trainer/admin/generate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ season_id: seasonId, topic: topic.trim(), level }) });
      toast.success(`Generated: ${d.title}`); setTopic(""); onCreated();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="AI lesson topic (e.g. How to handle a losing streak)"
        className="flex-1 min-w-[220px] bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none" data-testid={`gen-topic-${seasonId}`} />
      <select value={level} onChange={(e) => setLevel(e.target.value)} className="bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none">
        {["Beginner", "Intermediate", "Advanced", "Professional"].map((l) => <option key={l} value={l} className="bg-[#0A0A0A]">{l}</option>)}
      </select>
      <button onClick={gen} disabled={busy} className="btn-solid px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-60" data-testid={`gen-btn-${seasonId}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate lesson
      </button>
    </div>
  );
}

function Curriculum() {
  const [seasons, setSeasons] = useState([]);
  const [newSeason, setNewSeason] = useState({ title: "", level: "Beginner", description: "" });
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try { setSeasons(await api("/trainer/admin/seasons")); } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createSeason = async (e) => {
    e.preventDefault();
    try { await api("/trainer/admin/seasons", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...newSeason, order: (seasons.length + 1) * 10 }) }); setNewSeason({ title: "", level: "Beginner", description: "" }); setShowNew(false); load(); }
    catch (err) { toast.error(err.message); }
  };
  const delSeason = async (id) => { try { await api(`/trainer/admin/seasons/${id}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); } };
  const delLesson = async (id) => { try { await api(`/trainer/admin/lessons/${id}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); } };

  return (
    <div data-testid="admin-curriculum">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-zinc-500 font-light max-w-xl">Manage the Trading Trainer curriculum. Use the AI generator to draft new lessons in the MBM voice, then review them in the learner view.</p>
        <button onClick={() => setShowNew((s) => !s)} className="btn-solid px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="new-season-btn"><Plus className="h-4 w-4" /> New season</button>
      </div>

      {showNew && (
        <form onSubmit={createSeason} className="glass-card p-4 mb-6 flex flex-wrap items-center gap-2" data-testid="new-season-form">
          <input required placeholder="Season title" value={newSeason.title} onChange={(e) => setNewSeason({ ...newSeason, title: e.target.value })} className="flex-1 min-w-[180px] bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none" data-testid="new-season-title" />
          <select value={newSeason.level} onChange={(e) => setNewSeason({ ...newSeason, level: e.target.value })} className="bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none">
            {["Beginner", "Intermediate", "Advanced", "Professional"].map((l) => <option key={l} value={l} className="bg-[#0A0A0A]">{l}</option>)}
          </select>
          <input placeholder="Short description" value={newSeason.description} onChange={(e) => setNewSeason({ ...newSeason, description: e.target.value })} className="flex-1 min-w-[180px] bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none" />
          <button className="btn-solid px-4 py-2 text-sm" data-testid="new-season-submit">Create</button>
        </form>
      )}

      <div className="space-y-4">
        {seasons.map((s) => (
          <div key={s.id} className="glass-card p-5" data-testid={`admin-season-${s.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-zinc-100 font-medium">{s.title}</span>
                <span className="text-[11px] text-zinc-600 ml-2">· {s.level} · {(s.lessons || []).length} lessons {s.published ? "" : "· draft"}</span>
              </div>
              <button onClick={() => delSeason(s.id)} className="text-zinc-600 hover:text-red-400" data-testid={`del-season-${s.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 space-y-1.5">
              {(s.lessons || []).map((l) => (
                <div key={l.id} className="flex items-center gap-3 text-sm text-zinc-300 py-1.5 border-b border-white/5" data-testid="admin-lesson-row">
                  <span className="text-zinc-600 text-xs w-6">{Math.round(l.order / 10)}</span>
                  <span className="flex-1 truncate">{l.title} {l.ai_generated && <span className="text-[9px] text-[#9fd3ff]">· AI</span>}</span>
                  <span className="text-[10px] text-zinc-600">{(l.quiz || []).length} Q · {l.xp} XP</span>
                  <button onClick={() => delLesson(l.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            <GenerateLesson seasonId={s.id} onCreated={load} />
          </div>
        ))}
      </div>
    </div>
  );
}


function Revenue() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api("/billing/admin/revenue").then(setData).catch((e) => setErr(e.message));
  }, []);
  if (err) return <div className="text-sm text-red-400">{err}</div>;
  if (!data) return <div className="text-sm text-zinc-600">Loading…</div>;
  const fmtA = (n) => `A$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const stats = [
    { label: "Monthly Recurring Revenue", value: fmtA(data.mrr), sub: `${fmtA(data.arr)} annualised`, color: "#7ee0a1" },
    { label: "Active Subscriptions", value: data.active_paid, sub: `${data.active_comped} complimentary / manual`, color: "#9fd3ff" },
    { label: "New (30 days)", value: data.new_30d, sub: "paid subscriptions started", color: "#e6c67a" },
    { label: "Churn (30 days)", value: `${data.churn_rate}%`, sub: `${data.cancelled_30d} cancelled`, color: data.churn_rate > 10 ? "#ff7a7a" : "#7ee0a1" },
    { label: "Collected (30 days)", value: fmtA(data.collected_30d), sub: "payments received", color: "#b98cff" },
    { label: "Collected (all time)", value: fmtA(data.collected_total), sub: `${data.tx_count} payments`, color: "#d4d4d8" },
  ];
  return (
    <div data-testid="admin-revenue">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="glass-card p-5" data-testid={`revenue-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="section-label text-[9px] text-zinc-500">{s.label}</div>
            <div className="mt-2 font-heading text-2xl font-light" style={{ color: s.color }}>{s.value}</div>
            <div className="mt-1 text-[11px] text-zinc-500">{s.sub}</div>
          </div>
        ))}
      </div>
      <div className="mt-8">
        <div className="section-label text-[9px] text-zinc-500 mb-3">Recurring revenue by product</div>
        {data.breakdown.length === 0 ? (
          <p className="text-sm text-zinc-600" data-testid="revenue-empty">No paid subscriptions yet. Revenue will appear here as members subscribe.</p>
        ) : (
          <div className="space-y-2 max-w-2xl" data-testid="revenue-breakdown">
            {data.breakdown.map((b) => (
              <div key={`${b.kind}-${b.id}`} className="glass-card px-4 py-3 flex items-center gap-4">
                <span className="text-sm text-zinc-100 flex-1">{b.name}</span>
                <span className="text-[11px] text-zinc-500">{b.count} active</span>
                <span className="text-sm text-[#7ee0a1] w-28 text-right">{fmtA(b.mrr)}/mo</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="mt-8 text-[11px] text-zinc-600 max-w-2xl">
        MRR normalises weekly plans (×4.35) and yearly plans (÷12) to a monthly figure. Only
        Stripe-billed subscriptions count as revenue; complimentary and manually granted memberships
        are excluded. Churn = paid cancellations in the last 30 days against currently active paid subscriptions.
      </p>
    </div>
  );
}


function AiModels() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState("");
  const load = useCallback(() => api("/ai/settings").then(setData).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const setModel = async (feature, modelId) => {
    setSaving(feature);
    try {
      await api(`/ai/settings/${feature}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model_id: modelId }) });
      toast.success("AI model updated — takes effect immediately");
      await load();
    } catch (e) { toast.error(e.message); }
    setSaving("");
  };

  if (!data) return <div className="text-sm text-zinc-600">Loading…</div>;
  return (
    <div data-testid="admin-ai-models">
      <p className="text-sm text-zinc-500 font-light max-w-2xl mb-6">
        Choose which AI brain powers each feature. Claude and ChatGPT both run on your Emergent
        universal key — switching costs nothing and applies instantly, no code changes.
      </p>
      <div className="grid md:grid-cols-3 gap-4 max-w-4xl">
        {Object.entries(data.features).map(([key, f]) => (
          <div key={key} className="glass-card p-5" data-testid={`ai-feature-${key}`}>
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-[#9fd3ff]" />
              <span className="text-sm text-zinc-100 font-medium">{f.label}</span>
            </div>
            <p className="mt-1.5 text-[11px] text-zinc-500 min-h-[30px]">{f.desc}</p>
            <select value={f.model} disabled={saving === key}
              onChange={(e) => setModel(key, e.target.value)}
              className="mt-3 w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none disabled:opacity-50"
              data-testid={`ai-model-select-${key}`}>
              {data.models.map((m) => (
                <option key={m.id} value={m.id} className="bg-[#0A0A0A]">{m.label}</option>
              ))}
            </select>
            <div className="mt-2 text-[10px] text-zinc-600">
              Currently: <span className="text-[#9fd3ff]">{f.model_label}</span> ({f.provider === "openai" ? "OpenAI" : "Anthropic"})
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pipeline");
  useEffect(() => { document.title = "MBM · Admin HQ"; }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="grain" />
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="MBM home"><Logo className="h-5 w-auto" /></Link>
            <span className="text-zinc-700">/</span>
            <span className="section-label text-[10px] text-[#9fd3ff]">Admin HQ</span>
          </div>
          <span className="text-sm text-zinc-400">{user?.name}</span>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2 mb-8 border-b border-white/8 pb-2">
          <TabBtn id="pipeline" active={tab} onClick={setTab} icon={Kanban}>Sales Pipeline</TabBtn>
          <TabBtn id="onboarding" active={tab} onClick={setTab} icon={ClipboardList}>Onboarding</TabBtn>
          <TabBtn id="members" active={tab} onClick={setTab} icon={Users}>Members</TabBtn>
          <TabBtn id="revenue" active={tab} onClick={setTab} icon={Coins}>Revenue</TabBtn>
          <TabBtn id="pricing" active={tab} onClick={setTab} icon={Tag}>Pricing</TabBtn>
          <TabBtn id="curriculum" active={tab} onClick={setTab} icon={GraduationCap}>Curriculum</TabBtn>
          <TabBtn id="setups" active={tab} onClick={setTab} icon={LineChart}>Analysis</TabBtn>
          <TabBtn id="ai" active={tab} onClick={setTab} icon={Bot}>AI Models</TabBtn>
          <TabBtn id="applications" active={tab} onClick={setTab} icon={FileText}>Applications</TabBtn>
        </div>
        {tab === "pipeline" && <Pipeline />}
        {tab === "onboarding" && <OnboardingDash />}
        {tab === "members" && <Members />}
        {tab === "revenue" && <Revenue />}
        {tab === "pricing" && <PricingAdmin />}
        {tab === "curriculum" && <Curriculum />}
        {tab === "setups" && <AdminSetups />}
        {tab === "ai" && <AiModels />}
        {tab === "applications" && <Applications />}
      </div>
    </div>
  );
}
